import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components/macro';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';
import NextAction from '../Widget/NextAction';

import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';

import { Container } from '../DefaultStyles/Details';
import {
    Body,
    Content,
    Details,
    DetailsContainer,
    Footer,
    Title as QuestionText
} from '../DefaultStyles/Shared';

import { setExitingCardProps, getExitingCardProps } from '../Widget/actions';

import { ImageBanner } from '../../components/Link/Components';
import { isLongText } from '../../components/Shared/commonFunctions';
import { TransitionContainerNoExit } from '../../components/Transition/TranstionContainer';
import CheckIcon from '../../components/Svg/CheckIcon';
import ShareDrawer from '../../components/ShareDrawer';

import { formatEmoji } from '../../utils/fillDynamicText';
import { copyFor } from '../../copy/copy';
import { ActionTypes } from '../../utils/enums';
import { isPortrait } from '../../utils/size';
import ThemeContext from '../../utils/theming';

const AnswersWrapper = styled.div`
    float: left;
    flex: 1;
    display: flex;
    height: 100%;
    align-items: center;
    ${({ hasNextAction }) => hasNextAction && `margin-top: -40px;`};
    ${() =>
        isPortrait() &&
        `
            flex-direction: column;
            justify-content: center;
        `}
`;

const AnswerBar = styled.div`
    text-align: center;
    border-radius: 4px;
    margin: 0 8px 15px 8px;
    padding: 0 8px;
    float: left;
    position: relative;
    height: ${({ isMobileResolution, noOfAnswers }) =>
        isMobileResolution
            ? noOfAnswers === 4
                ? '46px'
                : '50px'
            : noOfAnswers === 4
            ? '58px'
            : '88px'};
    width: ${({ noOfAnswers }) =>
        noOfAnswers === 4
            ? 'calc(50% - 16px)'
            : noOfAnswers === 3
            ? 'calc(33.3% - 16px)'
            : 'calc(50% - 16px)'};
    border: ${({ answerAnswered, isCorrect, theme }) => {
        return isCorrect
            ? `solid 4px ${theme.colors.primary}`
            : answerAnswered
            ? `solid 4px ${theme.colors.secondary}`
            : `solid 1px #ccc`;
    }};
    color: ${({ isCorrect, theme }) => (isCorrect ? theme.colors.primary : '#000')};
    opacity: ${({ isCorrect, answerAnswered }) =>
        isCorrect || answerAnswered ? '1' : '0.4'};

    ${({ isMobileResolution, noOfAnswers }) =>
        isPortrait() &&
        `
            width: 100%;
            margin-bottom: 14px;
            height: ${
                isMobileResolution
                    ? noOfAnswers === 4
                        ? '46px'
                        : noOfAnswers === 2
                        ? '70px'
                        : '62px'
                    : noOfAnswers === 4
                    ? '56px'
                    : '88px'
            };
    `};
`;

const IconWrapper = styled.div`
    width: 24px;
    height: 24px;
    position: absolute;
    left: calc(50% - 12px);
    bottom: -16px;
    border-radius: 100%;
    background-color: ${({ theme }) => theme.colors.primary};
    line-height: 24px;
`;

const CloseIcon = styled.div`
    width: 22px;
    height: 38px;
    transform: rotate(45deg);
    font-family: Averta;
    font-size: 24px;
    line-height: 100%;
    text-align: left;
    color: #fff;
`;

const IconWrapperAnswered = styled.div`
    width: 24px;
    height: 24px;
    position: absolute;
    left: calc(50% - 12px);
    bottom: -16px;
    border-radius: 100%;
    background-color: ${({ theme }) => theme.colors.secondary};
    line-height: 24px;
`;

const PercentageBar = styled.div`
    font-size: 8px;
    font-weight: bold;
    line-height: 2.2;
    letter-spacing: 2px;
    text-align: center;
    position: relative;
    transform: translateY(-50%);
    top: ${({ noOfAnswers }) => (noOfAnswers === 4 ? '46%' : '48.5%')};

    @media (max-width: 30em) {
        top: ${({ isCorrect, answerAnswered }) =>
            isCorrect || answerAnswered ? '43%' : '50%'};
    }
`;

const PercentageText = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: ${({ isLongText, noOfAnswers }) =>
        isLongText && !noOfAnswers === 4 ? '13px' : noOfAnswers === 4 ? '14px' : '16px'};
    font-weight: 600;
    line-height: 0.92;
    letter-spacing: 0;
    text-transform: ${({ makeUppercase }) => (makeUppercase ? 'uppercase' : 'none')};

    @media (min-width: 30em) {
        margin-top: -3px;
    }

    .emoji {
        font-size: 22px;
    }
`;

const PercentageLabel = styled.span``;

const QuizSuccess = props => {
    const theme = useContext(ThemeContext);
    return (
        <TransitionContainerNoExit zIndex={15}>
            <Container
                desktopLinkImage={
                    !!props.desktopImageId && props.isVisualAction && !isPortrait()
                }
            >
                <Overlay />
                <OverlayPublisher />
                <OverlayPartner transitionType="right-to-left" />
                <Body>
                    <WidgetHeader />
                    <Content
                        flexGrow
                        isVisualAction={props.isVisualAction}
                        hasNextAction={props.hasNextAction}
                    >
                        <QuestionText
                            isShortText={!isLongText(props.feedbackText, 28)}
                            isLongText={isLongText(props.feedbackText, 60)}
                            isExtraLongText={isLongText(props.feedbackText, 95)}
                        >
                            {props.answeredCorrect
                                ? copyFor('feedback', 'correct', 'quiz')
                                : copyFor('feedback', 'incorrect', 'quiz')}
                            {props.feedbackText}
                        </QuestionText>
                        <DetailsContainer>
                            <Details style={{ position: `relative`, height: '100%' }}>
                                <AnswersWrapper>
                                    {props.results.map(r => (
                                        <AnswerBar
                                            key={r.id}
                                            theme={theme}
                                            isCorrect={r.isCorrect}
                                            answerAnswered={r.answerAnswered}
                                            noOfAnswers={props.noOfAnswers}
                                            isMobileResolution={props.isMobileResolution}
                                        >
                                            <PercentageBar
                                                noOfAnswers={props.noOfAnswers}
                                                isCorrect={r.isCorrect}
                                                answerAnswered={r.answerAnswered}
                                            >
                                                {!props.isMobileResolution && (
                                                    <PercentageLabel>
                                                        {copyFor(
                                                            'feedback',
                                                            'percentAnswered',
                                                            'quiz',
                                                            undefined,
                                                            { percentage: r.percentage }
                                                        )}{' '}
                                                        <br />
                                                    </PercentageLabel>
                                                )}
                                                <PercentageText
                                                    isLongText={isLongText(r.text)}
                                                    makeUppercase={
                                                        props.makeUppercase.length ===
                                                        props.noOfAnswers
                                                    }
                                                    noOfAnswers={props.noOfAnswers}
                                                    dangerouslySetInnerHTML={{
                                                        __html: formatEmoji(r.text)
                                                    }}
                                                />
                                            </PercentageBar>
                                            {r.isCorrect && (
                                                <IconWrapper theme={theme}>
                                                    <CheckIcon />
                                                </IconWrapper>
                                            )}
                                            {r.answerAnswered && !r.isCorrect && (
                                                <IconWrapperAnswered theme={theme}>
                                                    <CloseIcon>+</CloseIcon>
                                                </IconWrapperAnswered>
                                            )}
                                        </AnswerBar>
                                    ))}
                                </AnswersWrapper>
                            </Details>
                        </DetailsContainer>
                    </Content>
                    <NextAction
                        isVisual={props.isVisualAction}
                        beforeAdvancing={() => {
                            props.setExitingCardProps('quiz', props, props.activeAction);
                        }}
                        exitingAction={props.activeAction}
                    />
                    {props.isVisualAction && (
                        <ImageBanner
                            desktopImageId={props.desktopImageId}
                            mobileImageId={props.mobileImageId}
                        />
                    )}
                    <Footer>
                        <ShareDrawer newsletterSignUpVisible={true} />
                    </Footer>
                </Body>
            </Container>
        </TransitionContainerNoExit>
    );
};

QuizSuccess.propTypes = {
    activeAction: PropTypes.object.isRequired,
    answeredCorrect: PropTypes.bool.isRequired,
    desktopImageId: PropTypes.string,
    feedbackText: PropTypes.string,
    hasNextAction: PropTypes.bool.isRequired,
    isMobileResolution: PropTypes.bool,
    isVisualAction: PropTypes.bool,
    makeUppercase: PropTypes.array.isRequired,
    mobileImageId: PropTypes.string,
    noOfAnswers: PropTypes.number,
    results: PropTypes.array.isRequired,
    setExitingCardProps: PropTypes.func.isRequired
};

export default connect(
    (state, props) => {
        const exitingProps = getExitingCardProps(
            'quiz',
            ['/quiz/success'],
            props.location.pathname,
            state
        );
        if (exitingProps) return exitingProps;

        const buildResults = state => {
            if (state.quiz === null) {
                return [];
            } else {
                return state.quiz.results
                    .map(a => ({
                        ...a,
                        percentage:
                            state.quiz.quizAnswers !== undefined
                                ? state.quiz.quizAnswers.find(
                                      r => r.answerId === a.id
                                  ) !== undefined //eslint-disable-line
                                    ? state.quiz.quizAnswers.find(
                                          r => r.answerId === a.id
                                      ).percentage //eslint-disable-line
                                    : 0
                                : 0,
                        isCorrect: a.isCorrect,
                        answerAnswered: a.id === state.quiz.lastAnsweredQuiz.answerId
                    }))
                    .slice(0, 4);
            }
        };

        return {
            activeAction: state.widget.activeAction,
            isMobileResolution: state.widget.isMobileResolution,
            isVisualAction: state.widget.activeAction.isVisual,
            desktopImageId: state.widget.activeAction.desktopImageId,
            mobileImageId: state.widget.activeAction.mobileImageId,
            feedbackText: state.quiz.successInfo.feedbackText,
            noOfAnswers: state.quiz != null ? state.quiz.results.length : 0,
            answeredCorrect:
                state.quiz != null
                    ? state.quiz.results.find(r => r.isCorrect === true)?.id ===
                      state.quiz.lastAnsweredQuiz.answerId
                    : false,
            makeUppercase:
                state.quiz != null
                    ? state.quiz.results.filter(a => a.text.length <= 8)
                    : [],
            results: buildResults(state),
            hasNextAction: !!getNextAction(
                state.widget.actions,
                state.widget.activeAction.identifier,
                buildConditionalActionsAnswerHistory(state),
                state.widget.cards
            )
        };
    },
    { setExitingCardProps },
    null,
    {
        areStatesEqual: (next, prev) => {
            if (next.widget.activeAction.type !== ActionTypes.Quiz) return true;
            return next === prev;
        }
    }
)(QuizSuccess);
